import { useState, useEffect, useCallback, useRef } from 'react';
import { disableEnhancementTracking, enableEnhancementTracking } from '@/utils/enhancementService';
import { logConsent as logConsentToServer, logMultipleConsents } from '@/utils/consentLogging';

export interface ConsentPreferences {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  visitorId: boolean;
}

const CONSENT_KEY = 'cookie_consent_preferences';
const CONSENT_SHOWN_KEY = 'cookie_consent_shown';
const GPC_APPLIED_KEY = 'gpc_auto_applied';

const defaultPreferences: ConsentPreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  visitorId: false,
};

/**
 * Check if Global Privacy Control (GPC) signal is enabled
 */
const hasGPCSignal = (): boolean => {
  return navigator.globalPrivacyControl === true;
};

export const useCookieConsent = () => {
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const gpcCheckedRef = useRef(false);

  // Load saved preferences on mount and check for GPC signal
  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);
    const shown = localStorage.getItem(CONSENT_SHOWN_KEY);
    const gpcApplied = localStorage.getItem(GPC_APPLIED_KEY);
    
    // Check for GPC signal - auto opt-out of marketing/sharing if enabled
    if (hasGPCSignal() && !gpcApplied && !gpcCheckedRef.current) {
      gpcCheckedRef.current = true;
      
      // GPC detected: auto-reject marketing and visitor ID (sale/sharing)
      const gpcPrefs: ConsentPreferences = {
        essential: true,
        analytics: true, // Analytics is OK, just not sale/sharing
        marketing: false,
        visitorId: false,
      };
      
      setPreferences(gpcPrefs);
      setHasConsented(true);
      localStorage.setItem(CONSENT_KEY, JSON.stringify(gpcPrefs));
      localStorage.setItem(CONSENT_SHOWN_KEY, 'true');
      localStorage.setItem(GPC_APPLIED_KEY, 'true');
      
      // Disable enhancement tracking
      disableEnhancementTracking();
      
      // Log the GPC-triggered opt-out via edge function
      const anonymousId = localStorage.getItem('anonymous_id') || 'gpc-user';
      logMultipleConsents([
        { type: 'gpc_signal_detected', granted: false },
        { type: 'cookie_marketing', granted: false },
        { type: 'cookie_visitor_id', granted: false },
      ], anonymousId).then(() => {
        console.log('GPC signal detected - auto opted out of sale/sharing');
      });
      
      setIsLoading(false);
      return;
    }
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences({ ...defaultPreferences, ...parsed, essential: true });
        setHasConsented(true);
      } catch {
        // Invalid data, use defaults
      }
    }
    
    if (shown === 'true') {
      setHasConsented(true);
    }
    
    setIsLoading(false);
  }, []);

  const logConsent = useCallback(async (type: string, granted: boolean) => {
    try {
      // Use the secure edge function via consentLogging utility
      await logConsentToServer({
        type: type as 'cookie_analytics' | 'cookie_marketing' | 'cookie_visitor_id',
        granted,
      });
    } catch (error) {
      console.error('Failed to log consent:', error);
    }
  }, []);

  const savePreferences = useCallback(async (newPreferences: ConsentPreferences) => {
    const prefs = { ...newPreferences, essential: true };
    setPreferences(prefs);
    setHasConsented(true);
    
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    localStorage.setItem(CONSENT_SHOWN_KEY, 'true');

    // Log each consent type
    await Promise.all([
      logConsent('cookie_analytics', prefs.analytics),
      logConsent('cookie_marketing', prefs.marketing),
      logConsent('cookie_visitor_id', prefs.visitorId),
    ]);

    // Enable/disable enhancement tracking based on visitor ID consent
    if (prefs.visitorId) {
      enableEnhancementTracking();
    } else {
      disableEnhancementTracking();
    }

    // Dispatch event for other scripts to listen to
    window.dispatchEvent(new CustomEvent('consentUpdated', { detail: prefs }));
  }, [logConsent]);

  const acceptAll = useCallback(() => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      visitorId: true,
    });
  }, [savePreferences]);

  const rejectAll = useCallback(() => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      visitorId: false,
    });
  }, [savePreferences]);

  const updatePreference = useCallback((key: keyof ConsentPreferences, value: boolean) => {
    if (key === 'essential') return; // Cannot disable essential
    
    const newPrefs = { ...preferences, [key]: value };
    savePreferences(newPrefs);
  }, [preferences, savePreferences]);

  return {
    preferences,
    hasConsented,
    isLoading,
    savePreferences,
    acceptAll,
    rejectAll,
    updatePreference,
  };
};

// Utility to check if a specific consent is granted
export const hasConsent = (type: keyof ConsentPreferences): boolean => {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) return false;
    const prefs = JSON.parse(saved);
    return prefs[type] === true;
  } catch {
    return false;
  }
};
